import React from 'react';
import { startCase } from 'lodash';

import Chip from '~/components/core/Atomic/Chip/Chip';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import OverflowArrayTextDisplayWithTooltip from '~/components/core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import SortableTable from '~/components/core/Tables/SortableTable/index';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { getLobDescription } from '~/Utils/lobUtils';

import type { MoiEmailTemplateMetadata } from './types';

export const MOI_DEFAULT_LOCK_TOOLTIP = "The Default MOI Assignment template can't be disabled or deleted.";

export interface MoiEmailTablePaginationProps {
  page: number;
  limit: number;
}

interface MoiEmailTemplatesTablePropsType {
  templates: MoiEmailTemplateMetadata[];
  // Total row count from the server (includes the Default's virtual first slot).
  templatesCount: number;
  paginationProps: MoiEmailTablePaginationProps;
  onUpdatePaginationProps: (paginationProps: MoiEmailTablePaginationProps) => void;
  onSelectTemplate: (rowId: number) => void;
  onToggleEnabled: (templateId: number, newStatus: boolean) => Promise<void>;
  selectedRowId: number;
  // Id of the template whose toggle is mid-flight; its switch stays disabled until the update settles.
  togglingTemplateId?: number | null;
}

const MoiEmailTemplatesTable: React.FC<MoiEmailTemplatesTablePropsType> = ({
  templates,
  templatesCount,
  paginationProps,
  onUpdatePaginationProps,
  onSelectTemplate,
  onToggleEnabled,
  selectedRowId,
  togglingTemplateId = null,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();

  const columnData = [
    {
      id: 'template_name',
      disableSort: true,
      label: 'Name',
      specialCell: (template: MoiEmailTemplateMetadata) => (
        <div className="flex items-center gap-8">
          <OverflowTextWithToolTip maxWidth="220px">{template.template_name}</OverflowTextWithToolTip>
          {template.is_default ? <Chip size="small" label="Default" /> : null}
        </div>
      ),
    },
    {
      id: 'moi_methods',
      disableSort: true,
      label: 'Applies to MOI methods',
      specialCell: (template: MoiEmailTemplateMetadata) =>
        // The Default is the catch-all fallback — render "ALL" rather than its empty mapping list.
        template.is_default ? (
          'ALL'
        ) : (
          <OverflowArrayTextDisplayWithTooltip value={template.moi_methods.map((method) => method.display_name)} />
        ),
    },
    {
      id: 'lobs',
      disableSort: true,
      label: 'Line of Business',
      specialCell: (template: MoiEmailTemplateMetadata) =>
        !template.is_all_lobs && template.lobs && template.lobs.length > 0 ? (
          <>
            {template.lobs.map((lob) => (
              <Chip label={getLobDescription(lob, lobConfigurationsDict)} size="small" key={lob} className="mx-2" />
            ))}
          </>
        ) : (
          'All'
        ),
    },
    {
      id: 'coverages',
      disableSort: true,
      leftPaddingOnly: true,
      label: 'Coverages',
      specialCell: (template: MoiEmailTemplateMetadata) =>
        !template.is_all_coverages && template.coverage_keys && template.coverage_keys.length > 0 ? (
          <OverflowArrayTextDisplayWithTooltip
            value={template.coverage_keys}
            renderItem={(label) => (
              <Chip
                key={label}
                size="small"
                className="mx-2"
                label={<OverflowTextWithToolTip maxWidth="220px">{startCase(label)}</OverflowTextWithToolTip>}
              />
            )}
          />
        ) : (
          'All'
        ),
    },
    {
      id: 'last_update_datetime',
      disableSort: true,
      label: 'Last Updated',
      // A missing update time (e.g. the not-yet-materialized synthetic Default) is adapted to the
      // epoch — render blank rather than 1970 by keying on the value itself.
      specialCell: (template: MoiEmailTemplateMetadata) =>
        template.last_update_datetime.getTime() > 0 ? serverDateTimeToLocal(template.last_update_datetime) : '',
    },
    {
      id: 'is_enabled',
      disableSort: true,
      label: 'Enable',
      specialCell: (template: MoiEmailTemplateMetadata) => {
        const connectedToAutomaticRule = !!template.connected_automatic_rule_actions_display_names?.length;
        const isToggling = template.id === togglingTemplateId;
        const disabled = template.is_default || connectedToAutomaticRule || isToggling;
        const tooltip = template.is_default
          ? MOI_DEFAULT_LOCK_TOOLTIP
          : connectedToAutomaticRule
            ? 'Template cannot be disabled when connected to automatic rule'
            : '';
        return (
          <FsTooltip title={tooltip}>
            <span>
              <Switch
                checked={template.is_enabled}
                disabled={disabled}
                onChange={async (_, newStatus) => {
                  await onToggleEnabled(template.id, newStatus);
                }}
              />
            </span>
          </FsTooltip>
        );
      },
    },
  ];

  return (
    <SortableTable
      // Preserve the server-side ordering (Default first) — do not re-sort client-side.
      keepRowsOrder
      rows={templates}
      columns={columnData}
      onRowClick={onSelectTemplate}
      selectedRowsIds={[selectedRowId]}
      stickyHeader
      // Server-side pagination; SortableTable renders the pagination section on top by default.
      paginationProps={{
        page: paginationProps.page,
        rowsPerPage: paginationProps.limit,
        count: templatesCount,
        onChangePage: (_: unknown, newPage: number) => {
          onUpdatePaginationProps({ ...paginationProps, page: newPage });
        },
        onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
          onUpdatePaginationProps({ ...paginationProps, page: 0, limit: Number(event.target.value) });
        },
        rowsPerPageOptions: [10, 20, 30],
      }}
    />
  );
};

export default MoiEmailTemplatesTable;
